import { useQueryParams } from '@ytl/common-web';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useApp } from './contexts/appContext';
import { CurrentUserFetchState, selectCurrentFetchState, selectIsAuthenticated } from './contexts/currentUserSlicer';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ChangePasswordPage as ChangePasswordPageB2B } from './pages/ChangePasswordPageB2B';
import { EmailChangeBlockPage } from './pages/EmailChangeBlockPage';
import { EmailChangePage } from './pages/EmailChangePage';
import { EmailChangePage as EmailChangePageB2B } from './pages/EmailChangePageB2B';
import { EmailConfirmPage } from './pages/EmailConfirmPage';
import { ErrorPage } from './pages/ErrorPage';
import { ForgottenPasswordPage } from './pages/ForgottenPasswordPage';
import { ForgottenPasswordPage as ForgottenPasswordPageB2B } from './pages/ForgottenPasswordPageB2B';
import { HomePage } from './pages/HomePage';
import { HomePageB2B } from './pages/HomePageB2B';
import { LockoutPage } from './pages/LockoutPage';
import { MfaSetupPage } from './pages/MfaSetupPage';
import { NotConfirmedEmailPage } from './pages/NotConfirmedEmailPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { PhoneChangePage } from './pages/PhoneChangePage';
import { RegisterB2bPage } from './pages/RegisterB2bPage';
import { RegisterPage } from './pages/RegisterPage';
import { YeppFlowPage } from './pages/YeppFlowPage';
import { AppRoutes } from './routes';
import { OnboardingMethod } from './shared/@types/onboardingMethods';
import { Frame } from './shared/components/layout/frame/frame';
import { RegisterFrame } from './shared/components/layout/frame/registerFrame';
import { useIsB2B } from './shared/hooks/useIsB2B';

const UnAuthenticatedLayoutWithSession = ({
  page,
  disableHeader,
}: {
  page: React.ReactNode;
  disableHeader?: boolean;
}) => {
  return (
    <Frame
      children={page}
      isAuthenticated={false}
      disableHeader={disableHeader}
    />
  );
};

const AuthenticatedLayout = ({ page, disableHeader }: { page: React.ReactNode; disableHeader?: boolean }) => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUserFetchState = useSelector(selectCurrentFetchState);
  const { isLoading } = useApp();

  useEffect(() => {
    if (!isAuthenticated && currentUserFetchState === CurrentUserFetchState.Loaded) {
      navigate(AppRoutes.errorPage);
    }
  }, [navigate, isAuthenticated, isLoading, currentUserFetchState]);

  return (
    <>
      {isAuthenticated && (
        <Frame
          children={page}
          disableHeader={disableHeader}
          isAuthenticated={true}
        />
      )}
    </>
  );
};

const RegisterLayout = ({ page }: { page: React.ReactNode }) => {
  return (
    <>
      <RegisterFrame children={page} />
    </>
  );
};

const B2BAuthenticatedRoute = ({
  regular,
  b2b,
  disableHeader,
}: {
  regular: React.ReactNode;
  b2b: React.ReactNode;
  disableHeader?: boolean;
}) => {
  const isB2B = useIsB2B();
  return (
    <AuthenticatedLayout
      page={isB2B ? b2b : regular}
      disableHeader={disableHeader}
    />
  );
};

const B2BUnauthenticatedRoute = ({
  regular,
  b2b,
  disableHeader,
}: {
  regular: React.ReactNode;
  b2b: React.ReactNode;
  disableHeader?: boolean;
}) => {
  const isB2B = useIsB2B();
  return (
    <UnAuthenticatedLayoutWithSession
      page={isB2B ? b2b : regular}
      disableHeader={disableHeader}
    />
  );
};

export const AppRouting = () => {
  const query = useQueryParams();
  const isRegistrationCompletedRawLayout = query.get('checkout') === 'true';
  return (
    <>
      <Routes>
        <Route
          path={AppRoutes.nonActiveAccount}
          element={<UnAuthenticatedLayoutWithSession page={<NotConfirmedEmailPage />} />}
        />
        <Route
          path={AppRoutes.errorPage}
          element={<UnAuthenticatedLayoutWithSession page={<ErrorPage />} />}
        />
        <Route
          path={AppRoutes.changePassword}
          element={
            <B2BAuthenticatedRoute
              regular={<ChangePasswordPage />}
              b2b={<ChangePasswordPageB2B />}
            />
          }
        />
        <Route
          path={AppRoutes.forgottenPassword._}
          element={
            <B2BUnauthenticatedRoute
              regular={<ForgottenPasswordPage />}
              b2b={<ForgottenPasswordPageB2B />}
            />
          }
        />
        <Route
          path={AppRoutes.forgottenPasswordSet}
          element={
            <B2BUnauthenticatedRoute
              regular={<ChangePasswordPage isForgottenPasswordSetMode={true} />}
              b2b={<ChangePasswordPageB2B isForgottenPasswordSetMode={true} />}
            />
          }
        />
        <Route
          path={AppRoutes.emailConfirm}
          element={<UnAuthenticatedLayoutWithSession page={<EmailConfirmPage />} />}
        />
        <Route
          path={AppRoutes.emailChangeBlock}
          element={
            <UnAuthenticatedLayoutWithSession
              page={<EmailChangeBlockPage />}
              disableHeader={true}
            />
          }
        />
        <Route
          path={AppRoutes.registrationCompleted}
          element={<RegisterLayout page={<RegisterPage isRawLayout={isRegistrationCompletedRawLayout} />} />}
        />
        <Route
          path={AppRoutes.registerB2b}
          element={<RegisterLayout page={<RegisterB2bPage isRawLayout={false} />} />}
        />
        <Route
          path={AppRoutes.yeppRegister}
          element={<RegisterLayout page={<YeppFlowPage />} />}
        />
        <Route
          path={AppRoutes.onboarding}
          element={<RegisterLayout page={<OnboardingPage />} />}
        />
        <Route
          path={AppRoutes.kycOk}
          element={<RegisterLayout page={<OnboardingPage method={OnboardingMethod.KYC} />} />}
        />
        <Route
          path={AppRoutes.kycNok}
          element={<RegisterLayout page={<OnboardingPage method={OnboardingMethod.KYCError} />} />}
        />
        <Route
          path={AppRoutes.kycInit}
          element={<RegisterLayout page={<OnboardingPage method={OnboardingMethod.KYCInit} />} />}
        />
        <Route
          path={AppRoutes.lockout}
          element={<RegisterLayout page={<LockoutPage />} />}
        />
        <Route
          path={AppRoutes.mfaSetup}
          element={
            <AuthenticatedLayout
              page={<MfaSetupPage />}
              disableHeader={true}
            />
          }
        />
        <Route
          path={AppRoutes.emailChange}
          element={
            <B2BAuthenticatedRoute
              regular={<EmailChangePage />}
              b2b={<EmailChangePageB2B />}
            />
          }
        />
        <Route
          path={AppRoutes.phoneChange}
          element={<RegisterLayout page={<PhoneChangePage />} />}
        />
        <Route
          path={AppRoutes.smsLoginError}
          element={
            <B2BAuthenticatedRoute
              regular={<HomePage />}
              b2b={<HomePageB2B />}
            />
          }
        />
        <Route
          path={AppRoutes.yoloRegister}
          element={<RegisterLayout page={<RegisterPage isRawLayout={false} />} />}
        />

        <Route
          path="/"
          element={
            <B2BAuthenticatedRoute
              regular={<HomePage />}
              b2b={<HomePageB2B />}
            />
          }
        />
        <Route
          path="*"
          element={<NotFoundPage />}
        />
      </Routes>
    </>
  );
};
